import { usePresentationGenerationFlow } from "@/pages/home/hooks/use-presentation-generation-flow";
import { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useImportDesignSystem } from "../hooks/use-import-design-system";
import {
  composeChosenSlides,
  selectAllSettled,
  selectBuiltCount,
  selectIsImportPending,
} from "../stores/file-to-storydoc-generation.store";
import {
  getViewingImport,
  useImportRegistry,
} from "../stores/import-registry.store";
import {
  useImportStore,
  useImportStoreApi,
} from "../stores/import-store-context";
import { closeImportDialog } from "../utils/import-dialog-id";
import { openImportDialog } from "../utils/open-import-dialog";
import { trackImport } from "../utils/track-import";
import { GenerationCompleteToast } from "./generation-complete-toast";
import { GenerationToast } from "./generation-toast";
import { useRegenerationSync } from "./use-regeneration-sync";

const GENERATION_ERROR = "Couldn't generate your Storydoc — please try again.";

const isRegistered = (id: string) =>
  useImportRegistry.getState().imports.some((i) => i.id === id);

export const ImportRunner = ({
  id,
  hidden,
}: {
  id: string;
  hidden: boolean;
}) => {
  const store = useImportStoreApi();
  const { status, runId, rows, createdStory, dismissed, progressDismissed } =
    useImportStore((s) => ({
      status: s.status,
      runId: s.runId,
      rows: s.rows,
      createdStory: s.createdStory,
      dismissed: s.completeToastDismissed,
      progressDismissed: s.progressToastDismissed,
    }));
  const { style } = useImportDesignSystem();
  const { startGeneration, cancelActiveProcess } =
    usePresentationGenerationFlow();
  const activeRunRef = useRef<string | null>(null);
  const cancelRef = useRef(cancelActiveProcess);
  cancelRef.current = cancelActiveProcess;
  useRegenerationSync(store);

  const remove = () => {
    if (getViewingImport()?.id === id) closeImportDialog();
    useImportRegistry.getState().removeImport(id);
  };

  // startGeneration aborts this runner's previous process itself
  const run = (existingJobId?: string) => {
    const s = store.getState();
    if (!s.request) return;
    const token = s.runId;
    const isCurrent = () =>
      store.getState().runId === token && isRegistered(id);
    s.markStarted();
    activeRunRef.current = token;
    void startGeneration({
      cancelAriaLabel: "Cancel File to Storydoc",
      errorMessage: GENERATION_ERROR,
      loadingMessage: "Generating your Storydoc...",
      pollingMessage: "Generating your Storydoc...",
      suppressToast: true,
      existingJobId,
      request: {
        pdfUrl: s.request.pdfUrl,
        options: {
          pipeline: "original",
          pages: s.request.pages,
          fileName: s.fileName.slice(0, 255),
          fileImportV2: true,
          ...(s.request.designSystemId
            ? { designSystemId: s.request.designSystemId }
            : {}),
          ...(s.request.themed ? { themed: true } : {}),
        },
      },
      successMessage: "File to Storydoc is ready.",
      onEnqueued: (jobId) => {
        if (isCurrent()) store.getState().setJobId(jobId);
      },
      onPartial: (partial) => {
        if (!isCurrent()) return;
        const before = selectBuiltCount(store.getState().rows);
        store.getState().applyPartial(partial);
        const after = selectBuiltCount(store.getState().rows);
        // a resumed job already counted this on its first run
        if (!existingJobId && before === 0 && after > 0)
          trackImport(
            "options shown",
            { built: after, total: store.getState().rows.length },
            store,
          );
      },
      onSuccess: (result) => {
        if (isCurrent()) store.getState().applyResult(result);
      },
      onError: (msg) => {
        if (!isCurrent()) return;
        toast.error(msg);
        trackImport("error", { stage: "generation" }, store);
        remove();
      },
    });
  };

  useEffect(() => {
    const s = store.getState();
    if (s.status !== "generating" || s.startedJob || !s.request) return;
    run(s.jobId ?? undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, status]);

  // a superseding run (Continue with changed pages) ends the active one
  useEffect(() => {
    if (activeRunRef.current == null) return;
    if (activeRunRef.current === runId && status !== "idle") return;
    activeRunRef.current = null;
    cancelActiveProcess();
  }, [runId, status, cancelActiveProcess]);

  // Removed from the registry (accept, cancel, error, dropped): stop polling.
  // StrictMode's remount keeps the entry, so the run survives it.
  useEffect(
    () => () => {
      if (!isRegistered(id)) cancelRef.current();
    },
    [id],
  );

  const handleDismissProgress = () => {
    trackImport("toast dismissed", { status: "generating" }, store);
    store.getState().setProgressToastDismissed(true);
  };

  const handleReview = () => {
    trackImport("resumed", { via: "toast" }, store);
    openImportDialog({
      initialStep: "choosing",
      importId: id,
      entryPoint: store.getState().entryPoint,
    });
  };

  if (hidden) return null;
  const pending = selectIsImportPending({ status, createdStory });
  const progress = rows.length ? selectBuiltCount(rows) / rows.length : null;
  const firstChosen = composeChosenSlides(rows)[0] ?? null;

  if (status === "generating")
    return progressDismissed ? null : (
      <GenerationToast
        progress={progress}
        onOpen={handleReview}
        onDismiss={handleDismissProgress}
      />
    );
  if (status === "done" && pending && !dismissed && selectAllSettled(rows))
    return (
      <GenerationCompleteToast
        slide={firstChosen}
        style={style}
        onReview={handleReview}
        onDismiss={() => store.getState().setCompleteToastDismissed(true)}
      />
    );
  return null;
};
